import { useCallback, useEffect, useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { RotateCw, Settings2, X } from 'lucide-react'
import { useI18n } from '@/lib/i18n'
import Wordmark from '@/components/ui/Wordmark'
import NativeFrame from './NativeFrame'
import {
  purgeSandboxStorage,
  roleUrl,
  writeSession,
  type SandboxLang,
  type SandboxScenario,
  type SandboxSession,
  type SandboxTheme,
} from '@/lib/sandbox'
import { appBasePath, roleDef, roleForPath, type SandboxDevice, type SandboxProject } from '@/lib/sandboxProjects'

type Device = SandboxDevice
const ALL_DEVICES: Device[] = ['desktop', 'tablet', 'phone']
const DEVICE_WIDTH: Record<Device, number | null> = { desktop: null, tablet: 834, phone: 390 }

type Props = {
  project: SandboxProject
  session: SandboxSession
  onSessionChange: (s: SandboxSession | null) => void
}

type PanelCopy = Record<string, string>
type RoleCopy = { name: string }
type ProjectCopy = { name: string; shopLabel: string; roles: Record<string, RoleCopy> }

export default function SandboxFrame({ project, session, onSessionChange }: Props) {
  const { section, to } = useI18n()
  const navigate = useNavigate()
  const c = section('sandbox') as unknown as Record<string, string> & { panel: PanelCopy }
  const pc = section(`sandboxProjects.${project.id}`) as unknown as ProjectCopy
  const p = c.panel ?? {}

  const devices = project.devices ?? ALL_DEVICES
  const [device, setDevice] = useState<Device>('desktop')
  const [reloadKey, setReloadKey] = useState(0)
  const [path, setPath] = useState<string>('')
  // Path to reopen after a reload (settings apply / reload button); null = role home.
  const [keepPath, setKeepPath] = useState<string | null>(null)
  const [settingsOpen, setSettingsOpen] = useState(false)
  const frameRef = useRef<HTMLIFrameElement>(null)

  const update = useCallback(
    (patch: Partial<SandboxSession>, opts: { reload?: boolean } = {}) => {
      const next = { ...session, ...patch, shop: { ...session.shop, ...(patch.shop ?? {}) } }
      writeSession(project, next)
      onSessionChange(next)
      if (opts.reload) setReloadKey((k) => k + 1)
    },
    [project, session, onSessionChange],
  )

  const reload = () => {
    setKeepPath(path || null)
    setReloadKey((k) => k + 1)
  }

  const setRole = (role: string) => {
    if (role === session.role) return
    setKeepPath(null)
    update({ role }, { reload: true })
  }

  const resetData = () => {
    if (!window.confirm(p.resetConfirm)) return
    setKeepPath(null)
    update({ dataVersion: session.dataVersion + 1 }, { reload: true })
  }

  const endSession = () => {
    if (!window.confirm(p.endConfirm)) return
    purgeSandboxStorage(project)
    onSessionChange(null)
  }

  // Exit wipes the session too: the sandbox lives only while it is open.
  const exit = () => {
    purgeSandboxStorage(project)
    onSessionChange(null)
    navigate(to('/projects'))
  }

  const role = roleDef(project, session.role)
  const isNative = role.kind !== 'web'

  // Follow the iframe's own navigation (same origin): keep the role selector
  // and the path readout in sync, including cross-app redirects.
  useEffect(() => {
    if (isNative) return
    const id = window.setInterval(() => {
      try {
        const loc = frameRef.current?.contentWindow?.location
        if (!loc || loc.href === 'about:blank') return
        const full = loc.pathname + loc.search
        setPath((prev) => (prev === full ? prev : full))
        const r = roleForPath(project, loc.pathname)
        // Only a role change when the app itself changed (several roles may
        // share one web app).
        if (r && roleDef(project, r).app !== role.app) {
          setKeepPath(full)
          update({ role: r })
        }
      } catch {
        /* cross-origin or not ready */
      }
    }, 600)
    return () => window.clearInterval(id)
  }, [project, role.app, isNative, update])

  // Lock page scroll while the frame is showing.
  useEffect(() => {
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = prev
    }
  }, [])

  const base = appBasePath(project, role.app)
  const src = keepPath && keepPath.startsWith(base) ? keepPath : roleUrl(project, session.role)
  const width = isNative ? null : DEVICE_WIDTH[device]
  const shown = path.replace(base.replace(/\/$/, ''), '') || '/'
  const roleName = (r: string) => pc.roles?.[r]?.name ?? r
  const native = isNative ? project.native?.[role.app] : undefined

  return (
    <div className="sandbox-shell" data-device={isNative ? 'native' : device}>
      <header className="sb-panel">
        <div className="sb-panel__left">
          <Link to={to('/')} className="sb-panel__brand" aria-label={p.backToSite}>
            <Wordmark as="span" size={18} />
          </Link>
          <span className="sb-panel__title mono">
            <span className="sb-panel__dot" aria-hidden />
            Sandbox · {session.shop.name}
          </span>
        </div>

        <div className="sb-panel__center">
          <div className="seg seg--dark" role="radiogroup" aria-label={p.role}>
            {project.roles.map((r) => (
              <button key={r.key} type="button" role="radio" aria-checked={session.role === r.key} onClick={() => setRole(r.key)}>
                {roleName(r.key)}
              </button>
            ))}
          </div>
          {!isNative && (
            <span className="sb-panel__path mono" title={path}>
              {shown}
            </span>
          )}
        </div>

        <div className="sb-panel__right">
          {!isNative && (
            <div className="seg seg--dark sb-panel__device" role="radiogroup" aria-label={p.device}>
              {devices.map((d) => (
                <button key={d} type="button" role="radio" aria-checked={device === d} onClick={() => setDevice(d)}>
                  {p[d]}
                </button>
              ))}
            </div>
          )}
          <button type="button" className="sb-icon" onClick={reload} title={p.reload} aria-label={p.reload}>
            <RotateCw size={15} strokeWidth={1.75} />
          </button>
          <button
            type="button"
            className={`sb-icon ${settingsOpen ? 'is-active' : ''}`}
            onClick={() => setSettingsOpen((o) => !o)}
            aria-expanded={settingsOpen}
            aria-controls="sb-settings"
            title={p.settings}
            aria-label={p.settings}
          >
            <Settings2 size={15} strokeWidth={1.75} />
          </button>
          <button type="button" className="sb-icon sb-icon--text" title={p.exit} onClick={exit}>
            <X size={15} strokeWidth={1.75} />
            <span>{p.exit}</span>
          </button>
        </div>
      </header>

      {/* Always visible, on every role: this is a demo, not the product. */}
      <div className="sb-disclaimer" role="note">
        <span className="sb-disclaimer__tag mono">{p.disclaimerTag}</span>
        <span>{p.note}</span>
      </div>

      {settingsOpen && (
        <SettingsDrawer
          id="sb-settings"
          session={session}
          copy={c}
          shopLabel={pc.shopLabel}
          onClose={() => setSettingsOpen(false)}
          onApply={(patch, needsReset) => {
            setKeepPath(needsReset ? null : path || null)
            update(needsReset ? { ...patch, dataVersion: session.dataVersion + 1 } : patch, { reload: true })
            setSettingsOpen(false)
          }}
          onReset={resetData}
          onEnd={endSession}
        />
      )}

      <div className="sb-stage">
        <div className="sb-stage__frame" style={width ? { width, maxWidth: '100%' } : undefined}>
          {native ? (
            <NativeFrame key={`${role.app}-${reloadKey}`} app={native} lang={session.lang} title={`${pc.name} — ${roleName(session.role)}`} />
          ) : (
            <iframe
              key={`${role.app}-${reloadKey}`}
              ref={frameRef}
              className="sb-iframe"
              src={src}
              title={`${pc.name} — ${roleName(session.role)}`}
              allow="clipboard-write"
            />
          )}
        </div>
      </div>

      <footer className="sb-status mono">
        <span>{p.lifetime}</span>
      </footer>
    </div>
  )
}

type DrawerProps = {
  id: string
  session: SandboxSession
  copy: Record<string, string> & { panel: PanelCopy }
  shopLabel: string
  onClose: () => void
  onApply: (patch: Partial<SandboxSession>, needsReset: boolean) => void
  onReset: () => void
  onEnd: () => void
}

function SettingsDrawer({ id, session, copy: c, shopLabel, onClose, onApply, onReset, onEnd }: DrawerProps) {
  const p = c.panel ?? {}
  const [shopName, setShopName] = useState(session.shop.name)
  const [shopCity, setShopCity] = useState(session.shop.city)
  const [lang, setLang] = useState<SandboxLang>(session.lang)
  const [theme, setTheme] = useState<SandboxTheme>(session.theme)
  const [scenario, setScenario] = useState<SandboxScenario>(session.scenario)

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose()
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [onClose])

  const scenarioChanged = scenario !== session.scenario
  const shopChanged = shopName.trim() !== session.shop.name || shopCity.trim() !== session.shop.city

  return (
    <div className="sb-drawer" id={id} role="dialog" aria-label={p.settings}>
      <div className="sb-drawer__head">
        <span className="eyebrow eyebrow--plain">{p.settings}</span>
        <button type="button" className="sb-icon" onClick={onClose} aria-label={p.exit}>
          <X size={15} strokeWidth={1.75} />
        </button>
      </div>

      <label className="field">
        <span className="field__label">{shopLabel}</span>
        <input className="input" value={shopName} maxLength={40} onChange={(e) => setShopName(e.target.value)} />
      </label>
      <label className="field">
        <span className="field__label">{c.shopCity}</span>
        <input className="input" value={shopCity} maxLength={40} onChange={(e) => setShopCity(e.target.value)} />
      </label>

      <div className="field">
        <span className="field__label">{c.language}</span>
        <div className="seg" role="radiogroup" aria-label={c.language}>
          {(['de', 'en'] as const).map((l) => (
            <button key={l} type="button" role="radio" aria-checked={lang === l} onClick={() => setLang(l)}>
              {l === 'de' ? 'Deutsch' : 'English'}
            </button>
          ))}
        </div>
      </div>
      <div className="field">
        <span className="field__label">{c.theme}</span>
        <div className="seg" role="radiogroup" aria-label={c.theme}>
          {(['light', 'dark'] as const).map((t) => (
            <button key={t} type="button" role="radio" aria-checked={theme === t} onClick={() => setTheme(t)}>
              {t === 'light' ? c.light : c.dark}
            </button>
          ))}
        </div>
      </div>
      <div className="field">
        <span className="field__label">{c.scenario}</span>
        <div className="seg" role="radiogroup" aria-label={c.scenario}>
          {(['busy', 'empty'] as const).map((s) => (
            <button key={s} type="button" role="radio" aria-checked={scenario === s} onClick={() => setScenario(s)}>
              {s === 'busy' ? c.scenarioBusy : c.scenarioEmpty}
            </button>
          ))}
        </div>
        {(scenarioChanged || shopChanged) && <span className="mono muted sb-drawer__hint">{p.resetConfirm}</span>}
      </div>

      <div className="sb-drawer__actions">
        <button
          type="button"
          className="btn btn--primary btn--sm"
          onClick={() =>
            onApply(
              {
                lang,
                theme,
                scenario,
                shop: { name: shopName.trim() || session.shop.name, city: shopCity.trim() || session.shop.city },
              },
              scenarioChanged || shopChanged,
            )
          }
        >
          {p.apply}
        </button>
        <span className="mono muted">{p.applied}</span>
      </div>

      <hr className="rule" />

      <div className="sb-drawer__danger">
        <button type="button" className="btn btn--sm" onClick={onReset}>
          {p.reset}
        </button>
        <button type="button" className="btn btn--sm sb-drawer__end" onClick={onEnd}>
          {p.end}
        </button>
      </div>
    </div>
  )
}
